import { type Audience } from "@/lib/auth/currentUser";

export type ErrorSeverity = "info" | "warning" | "error" | "critical";

export type ErrorCategory =
  | "authentication"
  | "permission"
  | "integration"
  | "campaign"
  | "messaging"
  | "billing"
  | "validation"
  | "media"
  | "network"
  | "rate_limit"
  | "not_found"
  | "server"
  | "unknown";

export interface FriendlyError {
  title: string;
  description: string;
  severity?: ErrorSeverity;
  retryable?: boolean;
  category?: ErrorCategory;
  fixableBy?: "admin" | "support" | "anyone";
  action?: { label: string; to: string };
  whenCannotFix?: string;
}

export interface ApiErrorLike {
  error?: string | null;
  code?: string;
  message?: string;
  details?: string;
  meta?: { code?: string; subcode?: number | string; [k: string]: any } | null;
  statusCode?: number;
}

const CATALOG: Record<string, FriendlyError> = {
  WHATSAPP_WINDOW_EXPIRED: {
    title: "Reply window closed",
    description:
      "This chat's 24-hour reply window has closed. Send an approved template message to re-open the conversation.",
  },
  SOCIAL_WINDOW_EXPIRED: {
    title: "Reply window closed",
    description:
      "The 24-hour messaging window has expired. Wait for the customer to message again before replying.",
    severity: "warning",
  },
  WHATSAPP_NOT_CONNECTED: {
    title: "WhatsApp not connected",
    description:
      "This business account isn't connected to WhatsApp yet. Finish connecting your WhatsApp Business account before creating a campaign.",
    fixableBy: "admin",
    action: { label: "Connect WhatsApp", to: "/home/integrations/whatsapp-business" },
    whenCannotFix:
      "This business account isn't connected to WhatsApp yet. Ask an admin on your team to finish connecting it before creating a campaign.",
  },
  COMMERCE_STORE_URL_INVALID: {
    title: "Store address is invalid",
    description:
      "We couldn't reach your store because its saved address isn't valid (for example, an email address instead of yourstore.myshopify.com). Reconnect the store with the correct address.",
    category: "integration",
    fixableBy: "admin",
    action: { label: "Open integrations", to: "/home/integrations" },
    whenCannotFix:
      "We couldn't reach your store because its saved address isn't valid. Ask an admin on your team to reconnect the store with the correct address.",
  },
  WORKSPACE_REQUIRED: {
    title: "No workspace selected",
    description:
      "This screen reads data for one workspace, and none is selected yet. Pick a workspace and try again.",
    fixableBy: "user",
    action: { label: "Manage workspaces", to: "/home/workspaces" },
    whenCannotFix:
      "This screen needs a workspace. Ask an admin on your team to create one and give you access.",
  },
  BUSINESS_ACCOUNT_DISCONNECTED: {
    title: "Business account disconnected",
    description:
      "This business account is disconnected from Meta, so nothing can be created or synced on it. Reconnect it, or switch to a connected account, and try again.",
    fixableBy: "admin",
    action: { label: "Go to Business Accounts", to: "/home/integrations/whatsapp-business" },
    whenCannotFix:
      "This business account is disconnected from Meta. Ask an admin on your team to reconnect it, or switch to a connected account.",
  },
  BUSINESS_ACCOUNT_UNAVAILABLE: {
    title: "Business account unavailable",
    description:
      "This business account is no longer available. Select a connected WhatsApp Business account and try again.",
    fixableBy: "admin",
    action: { label: "Manage accounts", to: "/home/integrations/whatsapp-business" },
    whenCannotFix:
      "This business account is no longer available. Ask an admin on your team to connect a WhatsApp Business account.",
  },
  AGENT_NOT_IN_WORKSPACE: {
    title: "Agent isn't in this workspace",
    description:
      "This team member doesn't have access to this workspace, so the conversation can't be assigned to them. Pick someone from this workspace, or give them access in Team Management.",
  },
  CONTACT_DUPLICATE_PHONE: {
    title: "Contact already exists",
    description: "This phone number is already in your CRM. Open that contact instead of adding",
    category: "validation",
    severity: "warning",
  },
  CONTACT_BLOCKED: {
    title: "Contact is blocked",
    description:
      "This contact is blocked, so messages and actions are paused. Unblock them first to continue.",
  },
  TEMPLATE_NOT_ON_META: {
    title: "Template not approved yet",
    description:
      "This template isn't live on Meta yet. Wait for Meta to approve it, or pick an approved template.",
  },
  TEMPLATE_NOT_FOUND: {
    title: "Template not found",
    description: "We couldn't find that template. It may have been deleted or renamed.",
  },
  TEMPLATE_STALE: {
    title: "Template changed",
    description:
      "This template was updated somewhere else. Refresh the page to load the latest version before saving.",
  },
  TEMPLATE_CATALOG_UNAVAILABLE: {
    title: "Templates unavailable",
    description: "The template catalog couldn't load right now. Please try again in a moment.",
  },
  TEMPLATE_INCOMPLETE: {
    title: "Template is incomplete",
    description:
      "A required part of this template is missing or empty. Fill in the header, body, and button text, then try again.",
  },
  PRODUCT_NOT_IN_CATALOG: {
    title: "Product not in your catalog",
    description:
      "This product isn't published to your Meta catalog yet. Sync your catalog in Commerce (or pick a product that's live), then try again.",
  },
  CATALOG_NOT_CONNECTED: {
    title: "No catalog connected",
    description:
      "Connect or select a Meta catalog in Commerce settings to add or publish products.",
    fixableBy: "admin",
    whenCannotFix:
      "Ask an admin to connect a Meta catalog in Commerce settings to add or publish products.",
  },
  CATALOG_NOT_ACCESSIBLE: {
    title: "Catalog not accessible",
    description:
      "WhatsApp can't access this catalog with the connected account. Make sure the catalog belongs to the same Meta Business as this WhatsApp number and that the connected user has access to it in Meta Business Settings, then try again.",
    fixableBy: "admin",
    whenCannotFix:
      "WhatsApp can't access this catalog with the connected account. Ask an admin on your team to verify the catalog belongs to the same Meta Business and grant access in Meta Business Settings.",
  },

  ROLE_FORBIDDEN: {
    title: "Not allowed",
    description:
      "You don't have permission to do this. Ask an admin on your team if you need access.",
  },
  UNAUTHORIZED: {
    title: "Please sign in again",
    description: "Your session looks invalid. Sign in again to continue.",
  },
  FORBIDDEN: {
    title: "Not allowed",
    description: "You don't have permission to do this. Ask an admin if you need access.",
  },

  NEEDS_RECONNECT: {
    title: "Reconnect required",
    description:
      "This account's connection expired. Reconnect it in Integrations to keep using it.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix:
      "This account's connection expired. Ask an admin on your team to reconnect it in Integrations.",
  },
  ACCOUNT_ORG_MISMATCH: {
    title: "Account not in this workspace",
    description:
      "That account belongs to a different workspace and can't be used here.",
  },
  ACCOUNT_NOT_FOUND: {
    title: "Account not found",
    description: "We couldn't find that connected account. It may have been disconnected.",
  },
  DEV_TOKEN_NOT_APPROVED: {
    title: "Access pending approval",
    description:
      "This integration is still pending approval from the provider and can't run live actions yet.",
  },
  CSE_NOT_CONFIGURED: {
    title: "Search not available yet",
    description: "This feature isn't enabled for your workspace yet. Ask your administrator to turn it on.",
  },
  AI_NOT_CONFIGURED: {
    title: "AI not set up",
    description: "AI isn't set up for this workspace yet. Add the AI settings in Settings to continue.",
    fixableBy: "admin",
    action: { label: "Open Settings", to: "/home/settings" },
    whenCannotFix: "AI isn't set up for this workspace yet. Ask your administrator to add the AI settings.",
  },
  NO_AD_ACCOUNT: {
    title: "No ad account connected",
    description: "Connect an ad account first to use this feature.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix: "No ad account is connected. Ask an admin on your team to connect one first.",
  },
  NO_AI_PHONE_NUMBER: {
    title: "No AI phone number",
    description: "Set up an AI phone number before starting calls.",
  },

  RATE_LIMITED: {
    title: "Slow down a moment",
    description: "You've hit the rate limit. Wait a little while and try again.",
  },
  QUOTA_EXCEEDED: {
    title: "Daily limit reached",
    description: "You've used up the available quota for now. Try again later.",
  },
  META_RATE_LIMITED: {
    title: "Facebook data is temporarily unavailable",
    description:
      "This workspace has reached Meta's hourly limit for Facebook API requests. It resets automatically within the hour — please try again shortly.",
  },
  QUEUE_UNAVAILABLE: {
    title: "Service busy",
    description: "The processing queue is unavailable right now. Please try again shortly.",
  },

  FLOW_IN_USE: {
    title: "Flow is in use",
    description: "This flow is currently active and can't be changed. Pause it first.",
  },
  STATUS_IN_USE: {
    title: "Status in use",
    description: "This status is assigned to records and can't be deleted until they're moved.",
  },
  PHONE_ALREADY_IN_USE: {
    title: "Phone number already in use",
    description:
      "This phone number is already linked to another account. Enter a different number, or contact support if you think this is a mistake.",
    category: "validation",
    severity: "warning",
    fixableBy: "anyone",
  },
  EMAIL_TAKEN: {
    title: "Email already in use",
    description:
      "This email address is already linked to another account. Enter a different email, or contact support if you think this is a mistake.",
    category: "validation",
    severity: "warning",
    fixableBy: "anyone",
  },
  SITE_SLUG_CONFLICT: {
    title: "Address already taken",
    description: "That website address is already used. Pick a different one.",
  },

  INVALID_AMOUNT: {
    title: "Invalid amount",
    description: "Please enter a valid amount greater than 0.",
  },
  AMOUNT_INVALID_DECIMALS: {
    title: "Too many decimal places",
    description: "Amount can have at most 2 decimal places (e.g. 22.34).",
  },
  AMOUNT_TOO_LARGE: {
    title: "Amount too large",
    description: "This amount is too large to process. Please enter a smaller amount.",
  },

  RAZORPAY_NOT_CONNECTED: {
    title: "Razorpay not connected",
    description: "Connect Razorpay to start accepting payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect Razorpay", to: "/home/razorpay" },
    whenCannotFix: "Payments aren't set up yet. Ask an admin on your team to connect Razorpay.",
  },
  PAYU_NOT_CONNECTED: {
    title: "PayU not connected",
    description: "Connect PayU to start accepting payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect PayU", to: "/home/payu" },
    whenCannotFix: "Payments aren't set up yet. Ask an admin on your team to connect PayU.",
  },
  CASHFREE_NOT_CONNECTED: {
    title: "Cashfree not connected",
    description: "Connect Cashfree to start accepting payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect Cashfree", to: "/home/cashfree" },
    whenCannotFix: "Payments aren't set up yet. Ask an admin on your team to connect Cashfree.",
  },
  PAYMENT_NOT_CONNECTED: {
    title: "No payment method connected",
    description: "Connect a payment provider (Razorpay, PayU or Cashfree) to accept payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix: "Payments aren't set up yet. Ask an admin on your team to connect a payment provider.",
  },

  PAYMENT_PLAN_NOT_CONFIGURED: {
    title: "This plan isn't ready for checkout",
    description: "It isn't fully set up yet. Please contact support to enable it.",
    category: "billing",
  },
  PAYMENT_GATEWAY_NOT_CONFIGURED: {
    title: "Payments aren't set up yet",
    description: "Please contact support to enable payments for your account.",
    category: "billing",
  },
  PAYMENT_GATEWAY_ERROR: {
    title: "Payments are temporarily unavailable",
    description: "We couldn't connect to our payment service at the moment. Please try again in a few minutes, or contact support if the issue continues.",
    category: "billing",
    retryable: true,
    fixableBy: "support",
  },
  PLAN_NOT_FOUND: {
    title: "We couldn't find that plan",
    description: "Refresh the page and pick a plan again.",
    category: "billing",
  },
  SUBSCRIPTION_ACTION_FAILED: {
    title: "We couldn't update your plan",
    description: "Please try again in a moment, or contact support if it continues.",
    category: "billing",
    retryable: true,
  },

  PAYMENT_ALREADY_REFUNDED: {
    title: "Already refunded",
    description: "This payment was already refunded — no further action is needed.",
  },
  PAYMENT_NOT_CAPTURED: {
    title: "Payment not captured",
    description: "This payment hasn't been captured yet, so it can't be refunded.",
  },
  INSUFFICIENT_BALANCE: {
    title: "Insufficient balance",
    description:
      "There isn't enough balance to process this refund. Top up your payment account and retry.",
  },

  VIDEO_TOO_LARGE: {
    title: "Video too large",
    description: "This video exceeds the allowed size. Compress it or pick a smaller file.",
  },
  PHOTO_TOO_LARGE: {
    title: "Image too large",
    description: "This image exceeds the allowed size. Use a smaller file.",
  },
  FILE_TOO_LARGE: {
    title: "File too large",
    description:
      "This file is too large to upload. Use a smaller file (check the size limit shown in the uploader) and try again.",
    category: "media",
    severity: "warning",
  },
  TITLE_TOO_LONG: {
    title: "Title too long",
    description: "Shorten the title and try again.",
  },
  INVALID_VIDEO_FORMAT: {
    title: "Unsupported video",
    description: "This video format isn't supported. Use MP4 and try again.",
  },
  INVALID_PHOTO_FORMAT: {
    title: "Unsupported image",
    description: "This image format isn't supported. Use JPG or PNG and try again.",
  },

  OFFLINE: {
    title: "You're offline",
    description: "Your device isn't connected to the internet. Reconnect and try again.",
    severity: "warning",
    retryable: true,
    category: "network",
  },
  NETWORK_ERROR: {
    title: "Connection problem",
    description: "We couldn't reach the server. Check your internet connection and try again.",
    severity: "warning",
    retryable: true,
    category: "network",
  },
  TIMEOUT: {
    title: "Request timed out",
    description: "That took too long to respond. Please try again.",
  },
  MICROSERVICE_ERROR: {
    title: "Service error",
    description: "An internal service had a problem. Please try again in a moment.",
  },
  VALIDATION_ERROR: {
    title: "Check your input",
    description: "Some details look invalid. Review the form and try again.",
  },
  VALIDATION_FAILED: {
    title: "Check your input",
    description: "Some details look invalid. Review the form and try again.",
  },
  INTERNAL_ERROR: {
    title: "Something went wrong",
    description: "Something went wrong on our end. Please try again.",
    category: "server",
    severity: "error",
    retryable: true,
  },
  SESSION_CHECK_UNAVAILABLE: {
    title: "Temporary server problem",
    description: "We're having a temporary server problem. Please try again in a moment.",
    category: "server",
    severity: "warning",
    retryable: true,
  },

  PHONE_NUMBER_RESTRICTED: {
    title: "Number restricted",
    description:
      "This WhatsApp number is temporarily restricted by Meta. Check its status in WhatsApp Manager before sending again.",
  },
  PHONE_NUMBER_BANNED: {
    title: "Number banned",
    description:
      "Meta has banned this WhatsApp number. You can't send from it until it's reinstated — contact support.",
  },
  PHONE_QUALITY_LOW: {
    title: "Number quality is low",
    description:
      "This number's quality rating has dropped. Reduce blocks and spam reports to recover it before sending more campaigns.",
  },
  MESSAGE_LIMIT_REACHED: {
    title: "Daily message limit reached",
    description:
      "You've hit your WhatsApp messaging limit for now. It resets within 24 hours, or improve number quality to raise the limit.",
  },
  MARKETING_MESSAGE_BLOCKED: {
    title: "Marketing message blocked",
    description:
      "Meta limited this marketing message to protect the customer's experience. Try later or send it as a service message.",
  },
  USER_OPTED_OUT: {
    title: "Customer opted out",
    description:
      "This customer chose to stop receiving messages. You can't message them until they opt back in.",
  },
  RECIPIENT_NOT_ON_WHATSAPP: {
    title: "Not on WhatsApp",
    description:
      "This number isn't registered on WhatsApp, so the message can't be delivered. Double-check the number.",
  },
  INVALID_PHONE_NUMBER: {
    title: "Invalid phone number",
    description:
      "This phone number isn't in a valid format. Include the country code and remove spaces or symbols.",
  },
  TEMPLATE_PAUSED: {
    title: "Template paused",
    description:
      "This template was paused because its quality rating dropped. Use another approved template, or edit and resubmit this one.",
  },
  TEMPLATE_DISABLED: {
    title: "Template disabled",
    description:
      "This template was disabled for a policy reason and can't be used. Create a new one that follows WhatsApp's messaging rules.",
  },
  MEDIA_UPLOAD_EXPIRED: {
    title: "Media expired",
    description: "The uploaded media is no longer available. Re-upload the file and try again.",
  },
  UNSUPPORTED_MESSAGE: {
    title: "Unsupported message",
    description: "This type of message isn't supported here. Try a text, media, or template message.",
  },
  TEMPLATE_PARAMS_MISMATCH: {
    title: "Template details don't match",
    description:
      "The template's placeholders don't match the values provided. Refresh the template and re-enter the values.",
  },
  TEMPLATE_TOO_LONG: {
    title: "Template message too long",
    description:
      "The filled-in template exceeds WhatsApp's length limit. Shorten the variable values and try again.",
  },
  ACCOUNT_BILLING_ISSUE: {
    title: "Billing issue on this account",
    description:
      "This WhatsApp Business account has a billing or eligibility issue. Resolve it in WhatsApp Manager before sending.",
  },
  CANT_MESSAGE_RECIPIENT: {
    title: "Can't message this number",
    description:
      "WhatsApp won't deliver to this number right now. Confirm it's a valid recipient and not your own number.",
  },
  REQUEST_REJECTED: {
    title: "Request couldn't be processed",
    description: "We couldn't process your request. Please refresh the page and try again.",
  },

  INSTAGRAM_NOT_CONNECTED: {
    title: "Instagram not connected",
    description: "No Instagram account is connected. Connect one in Integrations to use this feature.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix: "No Instagram account is connected. Ask an admin on your team to connect one in Integrations.",
  },
  PAGE_DISCONNECTED: {
    title: "Facebook Page disconnected",
    description: "This Facebook Page is no longer connected. Reconnect it in Integrations to continue.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix: "This Facebook Page is no longer connected. Ask an admin on your team to reconnect it.",
  },
  INSTAGRAM_PERMISSION_DENIED: {
    title: "Missing permissions",
    description:
      "Some required Instagram/Facebook permissions are missing. Reconnect the account and accept all requested permissions.",
  },
  PAGE_PUBLISH_AUTH_FAILED: {
    title: "Publishing not authorized",
    description: "We aren't authorized to publish to this Page. Reconnect it and grant publishing access.",
  },
  ACCOUNT_RESTRICTED: {
    title: "Account restricted",
    description:
      "This account is temporarily restricted by Meta. Check its status in Meta Business Suite.",
  },
  CONTENT_POLICY_VIOLATION: {
    title: "Content not allowed",
    description: "Meta flagged this content as against its policies. Edit it and try again.",
  },
  POST_SCHEDULING_FAILED: {
    title: "Couldn't schedule post",
    description:
      "The post couldn't be scheduled. Check the date/time and the connected account, then try again.",
  },

  AD_ACCOUNT_DISABLED: {
    title: "Ad account disabled",
    description: "This ad account is disabled by Meta. Resolve the issue in Meta Ads Manager before running ads.",
  },
  AD_ACCOUNT_RESTRICTED: {
    title: "Ad account restricted",
    description: "This ad account is restricted. Check Meta Ads Manager for the required action.",
  },
  INSUFFICIENT_AD_BALANCE: {
    title: "Not enough ad balance",
    description: "There isn't enough balance to run this ad. Add funds to your ad account and try again.",
  },
  CAMPAIGN_BUDGET_EXCEEDED: {
    title: "Budget exceeded",
    description: "This campaign has used its full budget. Increase the budget to keep it running.",
  },
  CAMPAIGN_LIMIT_REACHED: {
    title: "Campaign limit reached",
    description: "You've reached the maximum number of campaigns. Pause or remove one before creating another.",
  },
  PIXEL_NOT_FOUND: {
    title: "Pixel not found",
    description: "No Meta Pixel is set up for this account. Add a Pixel to track conversions.",
  },
  AUDIENCE_TOO_SMALL: {
    title: "Audience too small",
    description: "This audience is too small to run an ad. Broaden your targeting and try again.",
  },

  FLOW_LOOP_DETECTED: {
    title: "Flow has a loop",
    description: "This flow loops back on itself and can't run. Remove the circular connection between steps.",
  },
  FLOW_PUBLISH_FAILED: {
    title: "Couldn't publish flow",
    description: "The flow couldn't be published. Make sure every step is complete and connected, then try again.",
  },
  FLOW_TRIGGER_MISSING: {
    title: "Flow needs a trigger",
    description: "Add a starting trigger to this flow before publishing it.",
  },
  FLOW_NODE_INVALID: {
    title: "A flow step is incomplete",
    description: "One of the steps is missing required details. Open each step and fill in the highlighted fields.",
  },
  FLOW_EXECUTION_FAILED: {
    title: "Flow run failed",
    description: "The flow couldn't finish running. Check its steps and try again.",
  },
  WEBHOOK_FAILED: {
    title: "Webhook failed",
    description: "We couldn't reach the webhook URL. Verify the endpoint is online and try again.",
  },

  WEBHOOK_TIMEOUT: {
    title: "Webhook didn't respond in time",
    description: "Your webhook URL took too long to reply. Make sure it's online and responds quickly, then try again.",
  },
  WEBHOOK_OVERRIDE_REJECTED: {
    title: "Meta wouldn't accept this webhook URL",
    description:
      "Meta checks the URL before saving it. Make sure it's reachable from the internet, that it answers Meta's verification request with the challenge value, and that the verify token matches.",
    category: "integration",
    severity: "warning",
    retryable: true,
  },
  WEBHOOK_SIGNATURE_INVALID: {
    title: "Couldn't verify the connection",
    description:
      "We couldn't securely verify data from the connected service. Reconnect the integration, or contact your administrator.",
  },
  THIRD_PARTY_SERVICE_DOWN: {
    title: "Service unavailable",
    description: "A connected service is temporarily down. Please try again in a little while.",
  },
  INTEGRATION_DISCONNECTED: {
    title: "Integration disconnected",
    description: "This integration is no longer connected. Reconnect it in Integrations to continue.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix:
      "This integration is no longer connected. Ask an admin on your team to reconnect it in Integrations.",
  },

  FILE_CORRUPTED: {
    title: "File is corrupted",
    description: "This file appears to be damaged and can't be used. Try a different copy.",
  },
  FILE_UPLOAD_FAILED: {
    title: "Upload failed",
    description: "The file couldn't be uploaded. Check your connection and try again.",
  },
  FILE_SCAN_FAILED: {
    title: "Couldn't scan file",
    description: "We couldn't safety-scan this file. Try again, or use a different file.",
  },
  MEDIA_PROCESSING_FAILED: {
    title: "Media processing failed",
    description: "We couldn't process this media. Try a different file or format.",
  },

  CONFIG_MISSING: {
    title: "Not set up yet",
    description: "This feature isn't fully set up. Finish configuring it in Settings to continue.",
    fixableBy: "admin",
    action: { label: "Open Settings", to: "/home/settings" },
    whenCannotFix: "This feature isn't fully set up. Ask your administrator to finish configuring it.",
  },
  AUTH_ERROR: {
    title: "Please sign in again",
    description: "Your session looks invalid. Sign in again to continue.",
  },
  GOOGLE_REJECTED: {
    title: "Request rejected",
    description: "Google didn't accept that request. Adjust your input and try again.",
  },
  INVALID_QUERY: {
    title: "Check your search",
    description: "That search wasn't valid. Adjust the terms and try again.",
  },
  TIKTOK_IDENTITY_DEPRECATED: {
    title: "Reconnect TikTok",
    description: "Your TikTok connection needs refreshing. Reconnect the account in Integrations to continue.",
  },
  VIDEO_CREATE_NOT_SUPPORTED: {
    title: "Video not supported here",
    description: "This account can't create video posts through this feature yet.",
  },
  INSTANTIATE_FAILED_ROLLED_BACK: {
    title: "Couldn't create it",
    description: "Something went wrong while creating this, so no changes were saved. Please try again.",
  },

  access_token_invalid: {
    title: "Reconnect required",
    description: "This account's connection has expired. Reconnect it in Integrations and try again.",
  },
  scope_not_authorized: {
    title: "Permission missing",
    description: "A required permission wasn't granted. Reconnect the account and accept all requested permissions.",
  },
  token_not_authorized_for_specified_publish_id: {
    title: "Reconnect TikTok",
    description: "This TikTok post can't be accessed with the current connection. Reconnect the account and try again.",
  },
  spam_risk_too_many_posts: {
    title: "TikTok daily post limit reached",
    description: "TikTok has temporarily capped how many posts this account can publish. Wait a while before posting again.",
    category: "rate_limit",
    severity: "info",
  },
  spam_risk_user_banned_from_posting: {
    title: "Posting blocked by TikTok",
    description: "TikTok has blocked this account from posting. Check the account's status in the TikTok app.",
  },
  reached_active_user_cap: {
    title: "Too many uploads at once",
    description: "TikTok is still processing your recent uploads. Wait for them to finish, then try again.",
  },
  unaudited_client_can_only_post_to_private_accounts: {
    title: "Public posting not enabled yet",
    description:
      "Until our TikTok app finishes review, posts can only go to private TikTok accounts. Set the account to private, or contact support.",
  },
  url_ownership_unverified: {
    title: "Verify your domain with TikTok",
    description:
      "TikTok needs the link's domain verified before it can be posted. Verify domain ownership in your TikTok developer settings.",
  },
  privacy_level_option_mismatch: {
    title: "Pick a privacy setting",
    description: "Choose a valid privacy level (public, friends, or private) for this TikTok post and try again.",
  },
  rate_limit_exceeded: {
    title: "Slow down a moment",
    description: "You've hit the rate limit. Wait a little while and try again.",
  },

  UNSUPPORTED_VIDEO_FORMAT: {
    title: "Unsupported video",
    description: "This video format isn't supported. Use MP4 (H.264) and try again.",
  },
  INVALID_VIDEO_SIZE: {
    title: "Video size not allowed",
    description: "This video's size is outside the allowed range. Use a smaller file and try again.",
  },
  VIDEO_DOWNLOAD_FAILED: {
    title: "Couldn't fetch the video",
    description: "We couldn't download the video to upload it. Check the file link and try again.",
  },
  EMPTY_VIDEO: {
    title: "Video file is empty",
    description: "The video file has no content. Re-export or pick a different file.",
  },
  TIKTOK_CONTENT_ERROR: {
    title: "TikTok couldn't process this",
    description: "TikTok rejected this content. Check the video/photo and caption, then try again.",
  },
  TIKTOK_ERROR: {
    title: "TikTok error",
    description: "TikTok returned an error. Please try again in a moment.",
  },

  YOUTUBE_NO_REFRESH_TOKEN: {
    title: "Reconnect YouTube",
    description: "Your YouTube connection needs refreshing. Disconnect and reconnect the channel in Integrations.",
  },
  YOUTUBE_REFRESH_NOT_AVAILABLE: {
    title: "Reconnect YouTube",
    description: "Your YouTube connection expired and can't refresh. Reconnect the channel in Integrations.",
  },
  YOUTUBE_TOKEN_EXCHANGE_FAILED: {
    title: "Couldn't connect YouTube",
    description: "We couldn't complete the YouTube connection. Try connecting the channel again.",
  },
  YOUTUBE_NO_ACCESS_TOKEN: {
    title: "Reconnect YouTube",
    description: "Your YouTube sign-in didn't return access. Reconnect the channel in Integrations.",
  },
  YOUTUBE_NO_CHANNEL: {
    title: "No YouTube channel found",
    description: "This Google account has no YouTube channel. Create one on YouTube, then connect again.",
  },
  YOUTUBE_CHANNEL_FETCH_FAILED: {
    title: "Couldn't load your channel",
    description: "We couldn't load your YouTube channel details. Try again in a moment.",
  },
  YOUTUBE_CHANNEL_IN_OTHER_ORG: {
    title: "Channel already connected",
    description: "This YouTube channel is already connected to another workspace. Disconnect it there first.",
  },
  YOUTUBE_INVALID_CHANNEL_RESPONSE: {
    title: "Couldn't read your channel",
    description: "YouTube returned unexpected channel data. Try reconnecting the channel.",
  },

  IMAGE_UPLOAD_FAILED: {
    title: "Image upload failed",
    description: "The image couldn't be uploaded. Check the file and try again.",
  },
  IMAGE_FETCH_FAILED: {
    title: "Couldn't fetch the image",
    description: "We couldn't load the image to attach it. Check the image link and try again.",
  },

  RAZORPAY_API_ERROR: {
    title: "Payment provider error",
    description: "Razorpay returned an error processing that. Please try again in a moment.",
  },
  RAZORPAY_INVALID_CREDENTIALS: {
    title: "Couldn't verify your Razorpay keys",
    description:
      "Razorpay rejected these keys. Double-check the Key ID and Key Secret from your Razorpay dashboard (Settings → API Keys) and try again.",
  },

  TOKEN_EXCHANGE_REJECTED: {
    title: "Shopify rejected these credentials",
    description:
      "Shopify wouldn't accept this Client ID and Client Secret. Check both against the app they came from, then try again.",
    category: "authentication",
    severity: "warning",
  },
  CLIENT_CREDENTIALS_NOT_APPLICABLE: {
    title: "Shopify rejected these credentials",
    description:
      "Shopify wouldn't accept this Client ID and Client Secret. Check both — they come from the same app, and the Client ID is not the secret — or switch to Access token and paste your custom app's Admin API access token.",
    category: "authentication",
    severity: "warning",
  },
  TOKEN_EXCHANGE_UNAUTHORIZED: {
    title: "Shopify rejected these credentials",
    description:
      "Shopify wouldn't authorize this Client ID and Client Secret. Check both, and that the app is installed on this store.",
    category: "authentication",
    severity: "warning",
  },
  SHOP_NOT_PERMITTED: {
    title: "Client credentials won't work for this store",
    description:
      "Shopify allows this method only when the app and the store belong to the same Shopify organization. Switch to Access token: in the Shopify admin, Settings → Apps and sales channels → Develop apps → create a custom app → copy its Admin API access token.",
    category: "integration",
    severity: "warning",
  },
  TOKEN_EXCHANGE_FAILED: {
    title: "Couldn't reach Shopify",
    description:
      "Shopify didn't answer the credentials check. Wait a moment and try again — your credentials may be fine.",
    category: "network",
    severity: "warning",
    retryable: true,
  },
  TOKEN_EXCHANGE_NO_TOKEN: {
    title: "Shopify sent no access token",
    description:
      "Shopify accepted the credentials but returned no access token. Try again, or connect with an Admin API access token instead.",
    category: "integration",
    retryable: true,
  },

  INVALID_SHOP_DOMAIN: {
    title: "That isn't a Shopify store domain",
    description:
      "Enter the permanent .myshopify.com address of the store — Shopify admin → Settings → Domains — for example cool-store.myshopify.com. An email address or your storefront domain won't work.",
    category: "validation",
    severity: "warning",
  },
  INVALID_ACCESS_TOKEN: {
    title: "Shopify rejected this access token",
    description:
      "Copy the Admin API access token again from the Shopify admin: Settings → Apps and sales channels → Develop apps → your app → API credentials. It begins with shpat_ and is shown only once, when the app is installed.",
    category: "authentication",
    severity: "warning",
  },
  SHOP_NOT_FOUND: {
    title: "No Shopify store at that domain",
    description:
      "Shopify has no store at this address. Check the shop domain — it's the .myshopify.com one under Settings → Domains, not your storefront domain.",
    category: "validation",
    severity: "warning",
  },
  SHOP_UNREACHABLE: {
    title: "Couldn't reach that store",
    description:
      "Nothing answered at this shop domain. Check it matches the .myshopify.com address in your Shopify admin, then try again.",
    category: "network",
    severity: "warning",
    retryable: true,
  },
  SHOP_PROBE_FAILED: {
    title: "Shopify couldn't confirm access",
    description:
      "Shopify didn't confirm access to this store — it may be paused, frozen for an unpaid bill, or temporarily unavailable. Check it opens in your Shopify admin, then try again.",
    category: "integration",
    severity: "warning",
    retryable: true,
  },

  TEMPLATE_LIMIT_REACHED: {
    title: "Template limit reached",
    description: "You've reached the maximum number of message templates. Delete an unused one, then try again.",
  },
  TEMPLATE_FIELD_TOO_LONG: {
    title: "Template text is too long",
    description: "A part of this template is over the character limit. Shorten the header, body, or footer and try again.",
  },
  TEMPLATE_FORMAT_INVALID: {
    title: "Check the template formatting",
    description: "The header, body, or footer formatting isn't valid. Review the template and try again.",
  },
  TEMPLATE_TOO_MANY_VARIABLES: {
    title: "Too many variables",
    description: "This template has too many {{variables}} for its length. Add more text, or remove some variables.",
  },
  TEMPLATE_VARIABLE_POSITION: {
    title: "Move the variable",
    description: "A {{variable}} can't be the very first or last thing in the template. Add some text around it.",
  },

  FLOW_BLOCKED: {
    title: "This flow is blocked",
    description: "Meta has blocked this WhatsApp flow, so it can't be used right now. Check its status in WhatsApp Manager.",
  },
  FLOW_THROTTLED: {
    title: "Flow is sending too fast",
    description: "This flow hit its hourly limit. Wait a little while, then try again.",
  },

  COUNTRY_RESTRICTED: {
    title: "Messaging restricted in this region",
    description: "Meta has restricted messaging to this recipient's country. Contact support if you need help.",
  },
  PAYMENTS_TOS_NOT_ACCEPTED: {
    title: "Accept the WhatsApp Payments terms",
    description: "WhatsApp Payments isn't fully set up. Accept the payments terms in WhatsApp Manager to continue.",
  },

  IG_MEDIA_FETCH_TIMEOUT: {
    title: "Couldn't fetch your media",
    description: "Instagram couldn't download your photo or video in time. Use a smaller file or a faster link, then try again.",
  },
  IG_IMAGE_TOO_LARGE: {
    title: "Image too large",
    description: "Instagram images must be under 8 MB. Use a smaller image and try again.",
  },
  IG_UNSUPPORTED_IMAGE: {
    title: "Unsupported image",
    description: "Instagram accepts JPG or PNG images. Convert the file and try again.",
  },
  IG_UNSUPPORTED_VIDEO: {
    title: "Unsupported video",
    description: "Instagram needs an MP4 (H.264) video. Convert the file and try again.",
  },
  IG_ASPECT_RATIO: {
    title: "Image shape not supported",
    description: "This image's shape isn't allowed by Instagram. Crop it between 4:5 (tall) and 1.91:1 (wide) and try again.",
  },
  IG_CAPTION_TOO_LONG: {
    title: "Caption too long",
    description: "Your caption is over Instagram's 2,200-character limit. Shorten it and try again.",
  },
  IG_DAILY_LIMIT_REACHED: {
    title: "Daily post limit reached",
    description: "You've hit Instagram's limit of 100 posts in 24 hours. Please try again later.",
  },
  IG_CONTENT_BLOCKED: {
    title: "Post blocked by Instagram",
    description: "Instagram flagged this content. Edit the image or caption and try again.",
  },
};

const PATTERN_MATCHERS: Array<{ test: RegExp; friendly: FriendlyError }> = [
  {
    test: /embedded credentials/i,
    friendly: CATALOG.COMMERCE_STORE_URL_INVALID,
  },
  {
    test: /2534022|2018278/,
    friendly: CATALOG.SOCIAL_WINDOW_EXPIRED,
  },
  {
    test: /2534022|outside (of )?allowed window|24[\s-]?hour/i,
    friendly: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  },
  {
    test: /\(#?190\)|access token.*(expire|invalid)|session has expired|OAuthException|token.*(expired|invalid)/i,
    friendly: CATALOG.NEEDS_RECONNECT,
  },
  {
    test: /\(#?10\)|\(#?200\)|do(es)? not have (the )?permission|insufficient permission/i,
    friendly: CATALOG.ROLE_FORBIDDEN,
  },
  {
    test: /application request limit reached|user request limit reached|\(#?4\)\s*application|\(#?80004\)/i,
    friendly: CATALOG.META_RATE_LIMITED,
  },
  {
    test: /rate limit|too many requests|#613|#80007|#131056|calls to this api have exceeded|spam rate limit|#131048/i,
    friendly: CATALOG.RATE_LIMITED,
  },

  {
    test: /\(#?131026\)|message undeliverable|recipient.*not.*whatsapp|not a (valid )?whatsapp user/i,
    friendly: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  },
  {
    test: /\(#?13104[79]\)|re-?engagement message|healthy ecosystem|maintain.*engagement/i,
    friendly: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  },
  {
    test: /\(#?131051\)|unsupported message type/i,
    friendly: {
      title: "Unsupported message",
      description: "This type of message isn't supported here. Try a text, media, or template message.",
    },
  },
  {
    test: /\(#?13105[23]\)|\(#?131057\)|media (download|upload) error|failed to (download|upload) media/i,
    friendly: CATALOG.MEDIA_UPLOAD_EXPIRED,
  },
  {
    test: /\(#?132015\)|template is paused/i,
    friendly: CATALOG.TEMPLATE_PAUSED,
  },
  {
    test: /\(#?132016\)|template is disabled/i,
    friendly: CATALOG.TEMPLATE_DISABLED,
  },
  {
    test: /\(#?132001\)|template (name )?does not exist|template not found|no.*matching template/i,
    friendly: CATALOG.TEMPLATE_NOT_ON_META,
  },
  {
    test: /\(#?132000\)|number of (localizable_)?params|param(eter)?s? (do(es)? not match|mismatch)/i,
    friendly: {
      title: "Template details don't match",
      description:
        "The template's placeholders don't match the values provided. Refresh the template and re-enter the values.",
    },
  },
  {
    test: /\(#?13200[57]\)|\(#?132012\)|template.*(policy|content|format).*(violat|invalid|mismatch)/i,
    friendly: CATALOG.CONTENT_POLICY_VIOLATION,
  },
  {
    test: /\(#?131031\)|\(#?368\)|account has been locked|temporarily blocked.*polic|business account.*restrict/i,
    friendly: CATALOG.ACCOUNT_RESTRICTED,
  },
  {
    test: /SQLState|ER_NO_SUCH_TABLE|Unknown column|SQL syntax|Table '[^']*' doesn'?t exist|doesn'?t exist; Error code|Deadlock found|ECONNREFUSED.*(mysql|3306|5432)/i,
    friendly: CATALOG.INTERNAL_ERROR,
  },
  {
    test: /Invalid JSON payload|Unknown name '[^']+' at|operations\[\d+\]\.(update|create|remove)/i,
    friendly: CATALOG.REQUEST_REJECTED,
  },
  {
    test: /no active razorpay connection|razorpay.*(not connected|isn'?t connected)|connect (your )?razorpay/i,
    friendly: CATALOG.RAZORPAY_NOT_CONNECTED,
  },
  {
    test: /no active payu connection|payu.*(not connected|isn'?t connected)|connect (your )?payu/i,
    friendly: CATALOG.PAYU_NOT_CONNECTED,
  },
  {
    test: /no active cashfree connection|cashfree.*(not connected|isn'?t connected)|connect (your )?cashfree/i,
    friendly: CATALOG.CASHFREE_NOT_CONNECTED,
  },
  {
    test: /no active connection found|no payment (gateway|provider|method).*(connected|configured)|payment (gateway|provider).*not connected/i,
    friendly: CATALOG.PAYMENT_NOT_CONNECTED,
  },
  {
    test: /not approved to access.*(REST|endpoint|protected)|protected customer data/i,
    friendly: {
      title: "Approval needed for this data",
      description:
        "This integration needs additional approval from the provider before it can access that data. Contact support to enable it.",
    },
  },
  {
    test: /no active shopify connection|shopify.*not connected|no shopify connection/i,
    friendly: {
      title: "Shopify not connected",
      description: "No Shopify store is connected. Go to Integrations → Shopify to connect your store.",
    },
  },
  {
    test: /failed to decrypt shopify|reconnect your shopify store/i,
    friendly: {
      title: "Reconnect your Shopify store",
      description: "Your Shopify connection needs to be refreshed. Go to Integrations → Shopify and reconnect.",
    },
  },
  {
    test: /shopify api error 429|shopify.*rate.?limit|exceeded.*shopify.*limit/i,
    friendly: {
      title: "Shopify rate limit",
      description: "Shopify is rate-limiting requests right now. Wait a moment and try again.",
      severity: "info",
      retryable: true,
    },
  },
  {
    test: /shop(ify)?.*(is frozen|frozen|has been (closed|paused|suspended)|store is unavailable|is closed)/i,
    friendly: {
      title: "Shopify store unavailable",
      description:
        "Your Shopify store is frozen or closed (often an unpaid Shopify bill). Resolve it in your Shopify admin, then try again.",
    },
  },
  {
    test: /shop_not_permitted/i,
    friendly: CATALOG.SHOP_NOT_PERMITTED,
  },
  {
    test: /(client credentials exchange|rejected these credentials|would not authorize these credentials)/i,
    friendly: CATALOG.CLIENT_CREDENTIALS_NOT_APPLICABLE,
  },
  {
    test: /shopify api error \d+/i,
    friendly: {
      title: "Shopify error",
      description: "Shopify returned an error. Check the product details and try again.",
    },
  },
  {
    test: /quotaexceeded|youtube.*quota|daily.*quota.*(exceed|reached)|exceeded your.*quota/i,
    friendly: CATALOG.QUOTA_EXCEEDED,
  },
  {
    test: /youtube.*(refresh token|disconnect and reconnect|reconnect)|cannot refresh youtube token|no refresh_token persisted/i,
    friendly: CATALOG.NEEDS_RECONNECT,
  },
  {
    test: /\(#?100\)|invalid parameter|GraphMethodException/i,
    friendly: {
      title: "Request couldn't be processed",
      description: "We couldn't process your request. Please refresh the page and try again.",
    },
  },
  {
    test: /unsupported (post|get) request|object with id .* does not exist|graph api documentation|developers\.facebook\.com\/docs/i,
    friendly: {
      title: "Something went wrong with Meta",
      description: "Meta couldn't complete this action. Your connected account may be unavailable or missing a permission. Refresh and try again, or reconnect it in Integrations.",
    },
  },
  {
    test: /endpoint request timed out|webhook.*tim(ed|e)?.?out/i,
    friendly: CATALOG.WEBHOOK_TIMEOUT,
  },
  {
    test: /query must be less than \d+ characters|(prompt|description|query).*(exceed|too long|less than).*\d+ character/i,
    friendly: {
      title: "Description is too long",
      description: "Your flow description is over the character limit. Shorten it and try again.",
      category: "validation",
      severity: "warning",
    },
  },
  {
    test: /aspect ratio|image.*(shape|ratio).*(not supported|unsupported)/i,
    friendly: CATALOG.IG_ASPECT_RATIO,
  },
  {
    test: /caption.*(exceed|too long|2,?200)|2,?200[\s-]?character/i,
    friendly: CATALOG.IG_CAPTION_TOO_LONG,
  },
  {
    test: /\b100 posts\b|posts? per (24 hours|day)|daily.*post.*limit|reached.*post.*limit/i,
    friendly: CATALOG.IG_DAILY_LIMIT_REACHED,
  },
  {
    test: /timeout downloading media|failed to (download|fetch) media|media fetch failed/i,
    friendly: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  },
  {
    test: /flow is (in a )?blocked( state)?|flow has been blocked/i,
    friendly: CATALOG.FLOW_BLOCKED,
  },
  {
    test: /template (count |limit ).*(exceed|reach)|maximum.*templates|reached.*template limit/i,
    friendly: CATALOG.TEMPLATE_LIMIT_REACHED,
  },
  {
    test: /product not found in catalog|product_retailer_id|product.*(not (found|exist)|does not exist).*catalog|catalog.*does not (contain|exist)/i,
    friendly: CATALOG.PRODUCT_NOT_IN_CATALOG,
  },
  {
    test: /catalog .*not accessible with the current token|catalog access denied/i,
    friendly: CATALOG.CATALOG_NOT_ACCESSIBLE,
  },
  {
    test: /\b(HEADER|BODY|FOOTER|BUTTONS?)\b.*(component|format).*(require|missing|empty)|requires '?\w+'? field|component.*is required/i,
    friendly: CATALOG.TEMPLATE_INCOMPLETE,
  },

  {
    test: /upload failed \(413\)|\b413\b[^]*(upload|too large|payload|entity)|payload too large|request entity too large|request too long|file (is )?too large|exceeds.*(file )?size limit|content length exceeded/i,
    friendly: CATALOG.FILE_TOO_LARGE,
  },
  {
    test: /no image url provided|image url (is )?required|(product )?image (is )?required|please (upload|add) (a |an |the )?(product )?image/i,
    friendly: {
      title: "Product image required",
      description: "Please upload a product image before creating the product.",
      category: "validation",
      severity: "warning",
    },
  },
  {
    test: /amount exceeds (the )?maximum|exceeds maximum amount|amount is too large|maximum amount allowed/i,
    friendly: CATALOG.AMOUNT_TOO_LARGE,
  },
  {
    test: /invalid razorpay credentials|razorpay.*(credential|key).*(invalid|rejected)|authentication failed.*razorpay/i,
    friendly: CATALOG.RAZORPAY_INVALID_CREDENTIALS,
  },
  {
    test: /failed to fetch|networkerror|err_connection|load failed|net::|connection refused|socket hang ?up|ECONNREFUSED|ECONNRESET|ENOTFOUND|EHOSTUNREACH|ENETUNREACH|EPIPE|read ECONN|write ECONN/i,
    friendly: CATALOG.NETWORK_ERROR,
  },
  {
    test: /timed out|timeout|ETIMEDOUT|ECONNABORTED/i,
    friendly: CATALOG.TIMEOUT,
  },
  {
    test: /internal server error|bad gateway|service unavailable|gateway timeout|\b50[0-4]\b|HTTP\s*5\d\d/i,
    friendly: {
      title: "Server problem",
      description: "We're having trouble on our end. Please try again in a few moments.",
    },
  },
  {
    test: /^[^.!?]{0,40}\bHTTP\s*4\d\d\b[^.!?]{0,12}$/i,
    friendly: {
      title: "Request couldn't be processed",
      description: "We couldn't process your request. Please refresh the page and try again.",
      category: "validation",
      severity: "warning",
    },
  },
];

const META_CODE_MAP: Record<number, FriendlyError> = {
  0: CATALOG.NEEDS_RECONNECT,
  3: CATALOG.NEEDS_RECONNECT,
  10: CATALOG.INSTAGRAM_PERMISSION_DENIED,
  102: CATALOG.NEEDS_RECONNECT,
  110: CATALOG.NEEDS_RECONNECT,
  190: CATALOG.NEEDS_RECONNECT,
  200: CATALOG.INSTAGRAM_PERMISSION_DENIED,
  2: CATALOG.THIRD_PARTY_SERVICE_DOWN,
  4: CATALOG.RATE_LIMITED,
  32: CATALOG.RATE_LIMITED,
  613: CATALOG.RATE_LIMITED,
  80007: CATALOG.RATE_LIMITED,
  130429: CATALOG.RATE_LIMITED,
  131048: CATALOG.RATE_LIMITED,
  131056: CATALOG.RATE_LIMITED,
  131057: CATALOG.RATE_LIMITED,
  368: CATALOG.ACCOUNT_RESTRICTED,
  131031: CATALOG.ACCOUNT_RESTRICTED,
  131042: CATALOG.ACCOUNT_BILLING_ISSUE,
  131000: CATALOG.INTERNAL_ERROR,
  131005: CATALOG.ROLE_FORBIDDEN,
  131008: CATALOG.VALIDATION_ERROR,
  131009: CATALOG.VALIDATION_ERROR,
  131016: CATALOG.THIRD_PARTY_SERVICE_DOWN,
  131021: CATALOG.CANT_MESSAGE_RECIPIENT,
  131026: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  1013: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  131045: CATALOG.ACCOUNT_BILLING_ISSUE, // phone registration / certificate issue → admin action
  131047: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  131049: CATALOG.MARKETING_MESSAGE_BLOCKED,
  131050: CATALOG.USER_OPTED_OUT, // recipient stopped marketing messages
  131051: CATALOG.UNSUPPORTED_MESSAGE,
  131052: CATALOG.MEDIA_UPLOAD_EXPIRED,
  131053: CATALOG.MEDIA_UPLOAD_EXPIRED,
  130472: CATALOG.MARKETING_MESSAGE_BLOCKED, // user in Meta experiment — message not delivered
  130497: CATALOG.COUNTRY_RESTRICTED,
  134011: CATALOG.PAYMENTS_TOS_NOT_ACCEPTED,
  135000: CATALOG.INTERNAL_ERROR,
  132000: CATALOG.TEMPLATE_PARAMS_MISMATCH,
  132001: CATALOG.TEMPLATE_NOT_ON_META,
  132005: CATALOG.TEMPLATE_TOO_LONG,
  132007: CATALOG.CONTENT_POLICY_VIOLATION,
  132012: CATALOG.TEMPLATE_PARAMS_MISMATCH,
  132015: CATALOG.TEMPLATE_PAUSED,
  132016: CATALOG.TEMPLATE_DISABLED,
  2388019: CATALOG.TEMPLATE_LIMIT_REACHED,
  2388040: CATALOG.TEMPLATE_FIELD_TOO_LONG,
  2388047: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388072: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388073: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388293: CATALOG.TEMPLATE_TOO_MANY_VARIABLES,
  2388299: CATALOG.TEMPLATE_VARIABLE_POSITION,
  132068: CATALOG.FLOW_BLOCKED,
  132069: CATALOG.FLOW_THROTTLED,
  9004: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207003: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  2207004: CATALOG.IG_IMAGE_TOO_LARGE,
  2207005: CATALOG.IG_UNSUPPORTED_IMAGE,
  2207006: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207008: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207009: CATALOG.IG_ASPECT_RATIO,
  2207010: CATALOG.IG_CAPTION_TOO_LONG,
  2207020: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207026: CATALOG.IG_UNSUPPORTED_VIDEO,
  2207032: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  2207042: CATALOG.IG_DAILY_LIMIT_REACHED,
  2207050: CATALOG.ACCOUNT_RESTRICTED,
  2207001: CATALOG.IG_CONTENT_BLOCKED,
  2207051: CATALOG.RATE_LIMITED,
  2207053: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  36001: CATALOG.IG_ASPECT_RATIO,
  36003: CATALOG.IG_ASPECT_RATIO,
  2534022: CATALOG.SOCIAL_WINDOW_EXPIRED,
  2018278: CATALOG.SOCIAL_WINDOW_EXPIRED,
};

const META_SUBCODE_MAP: Record<number, FriendlyError> = {
  33: CATALOG.INSTAGRAM_PERMISSION_DENIED, // code 100 + subcode 33 = missing scope
  458: CATALOG.NEEDS_RECONNECT, // user has not authorized application
  463: CATALOG.NEEDS_RECONNECT, // token expired
  467: CATALOG.NEEDS_RECONNECT, // invalid access token
  1404169: CATALOG.RATE_LIMITED, // IG DM 368 cooldown
  2207013: CATALOG.NEEDS_RECONNECT, // IG token/object expired
  2207052: CATALOG.MEDIA_UPLOAD_EXPIRED, // IG media download failed
  2534022: CATALOG.SOCIAL_WINDOW_EXPIRED, // IG/Messenger: outside the 24h window
  2018278: CATALOG.SOCIAL_WINDOW_EXPIRED, // Messenger: outside the standard window
};

function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function extractMetaCodeFromText(raw?: string): number | null {
  if (!raw) return null;
  const m = raw.match(/\(#?(\d{2,7})\)/);
  return m ? Number(m[1]) : null;
}

const HTTP_STATUS_MAP: Record<number, FriendlyError> = {
  400: {
    title: "Check your input",
    description: "Some details weren't accepted. Review what you entered and try again.",
    category: "validation",
    severity: "warning",
  },
  401: CATALOG.UNAUTHORIZED,
  403: CATALOG.ROLE_FORBIDDEN,
  404: {
    title: "Not found",
    description: "We couldn't find what you're looking for. It may have been moved or deleted.",
    category: "not_found",
    severity: "warning",
  },
  409: {
    title: "That conflicts with something",
    description: "This conflicts with something that already exists or is already running. Refresh and try again.",
    category: "validation",
    severity: "warning",
  },
  413: CATALOG.FILE_TOO_LARGE,
  422: CATALOG.VALIDATION_ERROR,
  429: CATALOG.RATE_LIMITED,
  500: CATALOG.INTERNAL_ERROR,
  502: {
    title: "Server problem",
    description: "We're having trouble on our end. Please try again in a few moments.",
    category: "server",
    severity: "error",
    retryable: true,
  },
  503: CATALOG.THIRD_PARTY_SERVICE_DOWN,
  504: CATALOG.TIMEOUT,
};

function inferFromCodeShape(code: string): FriendlyError | null {
  const c = code.toUpperCase();
  if (c === "NOT_FOUND" || c.endsWith("_NOT_FOUND"))
    return {
      title: "Not found",
      description: "We couldn't find what you're looking for. It may have been moved or deleted.",
      category: "not_found",
      severity: "warning",
    };
  if (/UNAUTHORIZED|TOKEN_EXPIRED|SESSION_|(^|_)AUTH(_|$)/.test(c))
    return { ...CATALOG.UNAUTHORIZED, category: "authentication", severity: "warning" };
  if (/FORBIDDEN|PERMISSION|NOT_ALLOWED|ACCESS_DENIED|(^|_)ROLE_/.test(c))
    return { ...CATALOG.ROLE_FORBIDDEN, category: "permission", severity: "warning" };
  if (/NETWORK|ECONN|FETCH/.test(c))
    return { ...CATALOG.NETWORK_ERROR };
  if (/RATE_LIMIT|TOO_MANY_REQUESTS|QUOTA|LIMIT_REACHED|LIMIT_EXCEEDED/.test(c))
    return { ...CATALOG.RATE_LIMITED, category: "rate_limit", severity: "info" };
  if (/NOT_CONFIGURED|CONFIG_MISSING|NOT_SET_UP|MISSING_CONFIG/.test(c))
    return { ...CATALOG.CONFIG_MISSING, category: "integration", severity: "warning" };
  if (/UNAVAILABLE|SERVICE_DOWN|(^|_)QUEUE_/.test(c))
    return { ...CATALOG.QUEUE_UNAVAILABLE, category: "server", severity: "warning", retryable: true };
  if (/TOO_LARGE|TOO_LONG|TOO_MANY|EXCEED/.test(c))
    return {
      title: "That's too large",
      description: "That's larger than allowed. Use a smaller value or file and try again.",
      category: "validation",
      severity: "warning",
    };
  if (/INVALID_|_INVALID$|MALFORMED|(^|_)BAD_/.test(c))
    return { ...CATALOG.VALIDATION_ERROR, category: "validation", severity: "warning" };
  if (/MISSING_|_REQUIRED$|REQUIRED_|EMPTY_|_EMPTY$/.test(c))
    return {
      title: "Missing required information",
      description: "Something required is missing. Fill in the highlighted fields and try again.",
      category: "validation",
      severity: "warning",
    };
  if (/CONFLICT|ALREADY_|_IN_USE$|(^|_)IN_USE/.test(c))
    return {
      title: "That already exists",
      description: "This conflicts with something that already exists. Refresh and try again.",
      category: "validation",
      severity: "warning",
    };
  if (/_FAILED$|FAILED_|_ERROR$|(^|_)ERROR_/.test(c))
    return {
      title: "Couldn't complete the action",
      description: "Something went wrong while completing your request. Please try again.",
      category: "unknown",
      severity: "error",
      retryable: true,
    };
  return null;
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

const RETRYABLE_TITLE_RE =
  /connection problem|you're offline|timed out|server problem|service (unavailable|busy)|slow down|temporarily/i;

function classify(friendly: FriendlyError): FriendlyError {
  const text = `${friendly.title} ${friendly.description}`.toLowerCase();

  let category = friendly.category;
  if (category == null) {
    if (/sign in|session|reconnect required|token/.test(text)) category = "authentication";
    else if (/permission|not allowed|ask an admin|ask your admin/.test(text)) category = "permission";
    else if (/rate limit|slow down|too many/.test(text)) category = "rate_limit";
    else if (/billing|balance|refund|payment/.test(text)) category = "billing";
    else if (/offline|connection problem|internet|reach the server/.test(text)) category = "network";
    else if (/not found/.test(text)) category = "not_found";
    else if (/server (problem|error)|unexpected error on our side/.test(text)) category = "server";
    else if (/connect|integration|reconnect it/.test(text)) category = "integration";
    else if (/template|message|campaign|whatsapp|opted out|reply window/.test(text)) category = "messaging";
    else if (/invalid|required|check your input|too (large|long)|already exists/.test(text)) category = "validation";
    else if (/media|file|image|video|upload/.test(text)) category = "media";
    else category = "unknown";
  }

  let severity = friendly.severity;
  if (severity == null) {
    if (/restricted|banned|disabled|locked|billing issue/.test(text)) severity = "critical";
    else if (category === "rate_limit") severity = "info";
    else if (category === "validation" || category === "permission" || category === "not_found")
      severity = "warning";
    else severity = "error";
  }

  const retryable = friendly.retryable ?? RETRYABLE_TITLE_RE.test(friendly.title);

  return { ...friendly, severity, retryable, category };
}

function humanizeFieldName(key: string): string {
  const words = key.replace(/[_-]+/g, " ").trim();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function detailsToText(details: unknown): string | undefined {
  if (details == null) return undefined;
  if (typeof details === "string") return details.trim() || undefined;
  if (typeof details === "object") {
    const d = details as Record<string, unknown>;
    const fieldMap =
      d.fields && typeof d.fields === "object" ? (d.fields as Record<string, unknown>) : null;
    if (fieldMap) {
      const parts = Object.entries(fieldMap)
        .filter(([, v]) => typeof v === "string" && (v as string).trim())
        .map(([k, v]) => `${humanizeFieldName(k)}: ${(v as string).trim()}`);
      if (parts.length) return parts.join("; ");
    }
    if (typeof d.message === "string" && d.message.trim()) return d.message.trim();
  }
  return undefined;
}

function humanizeCode(code: string): string {
  const words = code
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function looksLikeCode(s: string): boolean {
  return /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/.test(s.trim());
}

function defaultAskAdmin(category?: ErrorCategory): string {
  if (category === "integration" || category === "billing")
    return "This needs to be set up by an admin. Ask an admin on your team to take care of it.";
  return "You don't have permission to do this. Ask an admin on your team for help.";
}

function applyAudience(friendly: FriendlyError, audience: Audience | undefined): FriendlyError {
  if (!audience || friendly.fixableBy !== "admin") return friendly;
  const privileged = audience === "owner" || audience === "super_admin" || audience === "admin";
  if (privileged) return friendly;
  return {
    ...friendly,
    description: friendly.whenCannotFix ?? defaultAskAdmin(friendly.category),
    action: undefined,
  };
}

const DEV_JARGON_RE =
  /cannot read propert|is not a function|is not defined|undefined is not|null is not an object|\b(Type|Reference|Syntax|Range)Error\b|ECONN(REFUSED|RESET)|ETIMEDOUT|ENOTFOUND|EPIPE|socket hang up|getaddrinfo|\bER_[A-Z_]+\b|SQLSTATE|deadlock found|foreign key constraint|unexpected token|JSON at position|at \S+ \(.+:\d+:\d+\)|maximum call stack|heap out of memory/i;

export function resolveFriendlyError(
  input: ApiErrorLike | Error | string | null | undefined,
  fallback?: Partial<FriendlyError>,
  audience?: Audience,
): FriendlyError & { raw: string | null } {
  const fb: FriendlyError = {
    title: fallback?.title || "Something went wrong",
    description:
      fallback?.description || "We couldn't complete that action. Please try again.",
  };

  if (input == null) return { ...classify(fb), raw: null };

  let code: string | undefined;
  let metaCode: string | undefined;
  let rawMessage: string | undefined;
  let numericCode: number | null = null;
  let numericSubcode: number | null = null;
  let statusCode: number | null = null;

  const axiosOf = (o: unknown) => {
    const resp = (o as Record<string, unknown>)?.["response"] as Record<string, unknown> | undefined;
    const data = resp?.["data"] as Record<string, unknown> | undefined;
    return {
      error: typeof data?.["error"] === "string" ? (data["error"] as string) : undefined,
      message: typeof data?.["message"] === "string" ? (data["message"] as string) : undefined,
      details: typeof data?.["details"] === "string" ? (data["details"] as string) : undefined,
      code: data?.["code"],
      status: toNum(resp?.["status"]),
    };
  };

  let detailsText: string | undefined;

  if (typeof input === "string") {
    rawMessage = input;
  } else if (input instanceof Error) {
    const e = input as Error & Record<string, unknown>;
    const ax = axiosOf(e);
    rawMessage = ax.error ?? ax.message ?? input.message;
    detailsText = detailsToText(e.details) ?? ax.details;
    code = typeof e.code === "string" ? (e.code as string) : typeof ax.code === "string" ? (ax.code as string) : undefined;
    numericCode = toNum(e.code) ?? toNum(e.metaCode) ?? toNum(ax.code);
    numericSubcode = toNum(e.error_subcode) ?? toNum(e.metaErrorSubcode) ?? toNum(e.metaSubcode);
    statusCode = toNum(e.statusCode) ?? toNum(e.status) ?? ax.status;
  } else {
    const bag = input as Record<string, unknown>;
    const ax = axiosOf(bag);
    code = typeof input.code === "string" ? input.code : typeof ax.code === "string" ? ax.code : undefined;
    metaCode = typeof input.meta?.code === "string" ? input.meta.code : undefined;
    rawMessage = input.error || input.message || ax.error || ax.message || undefined;
    detailsText = detailsToText(input.details) ?? ax.details;
    numericCode = toNum(input.meta?.code) ?? toNum(input.code) ?? toNum(ax.code);
    numericSubcode = toNum(input.meta?.subcode) ?? toNum(bag.error_subcode);
    statusCode = toNum(input.statusCode) ?? toNum(bag.status) ?? ax.status;
  }

  const done = (friendly: FriendlyError, raw: string | null) => ({
    ...applyAudience(classify(friendly), audience),
    raw,
  });

  const isGenericValidationCode =
    code === "VALIDATION_FAILED" ||
    code === "VALIDATION_ERROR" ||
    metaCode === "VALIDATION_FAILED" ||
    metaCode === "VALIDATION_ERROR";
  if (
    isGenericValidationCode &&
    detailsText &&
    detailsText.trim() &&
    !looksLikeCode(detailsText) &&
    !DEV_JARGON_RE.test(detailsText)
  ) {
    return done(
      {
        title: "Check your input",
        description: detailsText,
        category: "validation",
        severity: "warning",
      },
      detailsText,
    );
  }

  const RAW_MESSAGE_WINS_CODES = new Set([
    "PUBLISH_FAILED_LOCALLY",
    "META_REJECTED_PRODUCT",
    "PRODUCT_HAS_NO_IMAGE",
  ]);
  if (
    code &&
    RAW_MESSAGE_WINS_CODES.has(code) &&
    rawMessage &&
    rawMessage.trim() &&
    !DEV_JARGON_RE.test(rawMessage)
  ) {
    return done({ title: fb.title, description: rawMessage.trim() }, rawMessage);
  }

  for (const c of [metaCode, code]) {
    if (c && CATALOG[c]) return done(CATALOG[c], rawMessage ?? c);
  }

  const codeFromText = extractMetaCodeFromText(rawMessage);
  if (numericSubcode != null && META_SUBCODE_MAP[numericSubcode])
    return done(META_SUBCODE_MAP[numericSubcode], rawMessage ?? `#${numericSubcode}`);
  for (const nc of [numericCode, codeFromText]) {
    if (nc != null && META_CODE_MAP[nc]) return done(META_CODE_MAP[nc], rawMessage ?? `#${nc}`);
  }

  const searchText = [rawMessage, detailsText].filter(Boolean).join(" ");
  if (searchText) {
    for (const { test, friendly } of PATTERN_MATCHERS) {
      if (test.test(searchText)) {
        if (friendly === CATALOG.NETWORK_ERROR && isOffline()) return done(CATALOG.OFFLINE, searchText);
        return done(friendly, detailsText ?? rawMessage ?? null);
      }
    }
  }

  const shapeCode = metaCode || code || (rawMessage && looksLikeCode(rawMessage) ? rawMessage : undefined);
  if (shapeCode) {
    const inferred = inferFromCodeShape(shapeCode);
    if (inferred) return done(inferred, rawMessage ?? shapeCode);
  }

  if (statusCode != null && HTTP_STATUS_MAP[statusCode]) {
    const serverish = statusCode >= 500 || statusCode === 401 || statusCode === 403 || statusCode === 429;
    const hasHumanProse = !!(rawMessage && rawMessage.trim() && !looksLikeCode(rawMessage));
    if (serverish || !hasHumanProse) return done(HTTP_STATUS_MAP[statusCode], rawMessage ?? `HTTP ${statusCode}`);
  }

  if (shapeCode) {
    const humanized = humanizeCode(shapeCode);
    if (humanized) return done({ title: fb.title, description: `${humanized}.` }, rawMessage ?? shapeCode);
  }

  const isProse = (s?: string) => !!(s && s.trim() && !looksLikeCode(s));
  const proseCandidate = isProse(detailsText) ? detailsText : isProse(rawMessage) ? rawMessage : null;
  if (proseCandidate) {
    if (DEV_JARGON_RE.test(proseCandidate)) return done(fb, proseCandidate);
    const cleaned = proseCandidate.replace(/[:\-\s]+(undefined|null)\s*$/i, "").trim();
    if (cleaned) return done({ title: fb.title, description: cleaned }, proseCandidate);
  }

  return done(fb, rawMessage ?? null);
}
